const R = (f) => `/images/real/${f}`;

export const pages = {
  lodge: {
    path: "/private-island-accommodation-westport/",
    eyebrow: "The Island Lodge",
    seo: {
      title: "Group Accommodation Westport Sleeps 22 | Collanmore Island Lodge",
      description:
        "Exclusive group accommodation near Westport for up to 22 guests. Private island lodge with traditional bar, kitchen, sauna and hot tub in Clew Bay.",
      path: "/private-island-accommodation-westport/",
    },
    h1: "Exclusive Private Island Accommodation for Up to 22 Guests",
    heroImage: R("lodge-living.jpg"),
    heroImageAlt: "The communal living and dining space inside Collanmore Island Lodge",
    heroParas: [
      "Collanmore Island Lodge offers an unforgettable alternative to traditional group accommodation in Ireland.",
      "Situated on beautiful Collanmore Island in Clew Bay, our exclusive-use lodge combines the comfort of a welcoming holiday property with the freedom and adventure of an island getaway.",
      "Ideal for groups of friends, families, celebrations and corporate guests, the lodge sleeps up to 22 people across four group bedrooms.",
    ],
    cta: { label: "Enquire About the Lodge", to: "/contact/" },
    sections: [
      {
        h2: "Comfortable Group Accommodation",
        paras: [
          "The lodge features four bedrooms with a combination of double sleeping pods, double beds and single beds.",
          "The flexible group accommodation is particularly suited to friends, families and organised group stays.",
        ],
        image: R("bunk-room.jpg"),
        imageAlt: "The group bunk room with timber bunks and garden views",
      },
      {
        h2: "Lodge Facilities",
        list: [
          "Exclusive use of the lodge for your booked group.",
          "Accommodation for up to 22 overnight guests.",
          "Four group bedrooms.",
          "Fully equipped self-catering kitchen.",
          "Fridges and freezer.",
          "Wi-Fi.",
          "Smart television.",
          "Traditional Irish-style private bar.",
          "Cocktail-style bar area.",
          "Comfortable communal living and dining spaces.",
          "Outdoor gathering and BBQ areas.",
          "Fire pit.",
          "Access to the beach and island surroundings.",
          "Hot tub access at designated times.",
          "Optional beach sauna experiences.",
          "High-speed boat transfers from Rosmoney Pier.",
        ],
      },
      {
        h2: "Tom's Bar — Your Very Own Irish Pub",
        paras: [
          "There's something special about having your own traditional Irish pub for the weekend.",
          "Tom's Bar creates a relaxed, welcoming setting where friends can gather, celebrate and enjoy their favourite drinks.",
          "Bring your own beverages, arrange approved kegs in advance and enjoy a unique private bar experience.",
        ],
        image: R("lodge-bar.jpg"),
        imageAlt: "Tom's Bar — the traditional Irish-style private bar at Collanmore Island Lodge",
      },
      {
        h2: "Make the Weekend Your Own",
        paras: [
          "Choose a relaxed self-catering break or add activities, entertainment and catering to create a fully planned group getaway.",
          "Options include sea kayaking, paddleboarding, sauna sessions, Chinese banquet catering and live music.",
        ],
        image: R("lodge-deck-day.jpg"),
        imageAlt: "The lodge sun deck with picnic tables overlooking Clew Bay",
      },
    ],
    finalHeading: "Enquire About the Lodge",
  },

  hen: {
    path: "/hen-parties-westport/",
    eyebrow: "Hen Parties",
    seo: {
      title: "Hen Parties Westport | Private Island Hen Weekends Mayo",
      description:
        "Plan a memorable hen party near Westport on an exclusive island lodge sleeping up to 22, with private bars, sauna, kayaking, catering and live music.",
      path: "/hen-parties-westport/",
    },
    h1: "An Unforgettable Private Island Hen Party in Westport",
    heroImage: R("hen-party-lodge.jpg"),
    heroImageAlt: "A beach-themed hen party gathered in the lodge dining room",
    heroParas: [
      "Looking for a hen party destination that's completely different?",
      "Swap the usual hotel or city break for an exclusive private island lodge in the heart of Clew Bay.",
      "At Collanmore Island Lodge, your hen party group can enjoy a weekend of adventure, laughter, relaxation and celebrations, all in one incredible location.",
      "With accommodation for up to 22 guests, private bar facilities and a range of optional experiences, it's the perfect setting for a hen weekend to remember.",
    ],
    cta: { label: "Plan Your Hen Party", to: "/contact/" },
    sections: [
      {
        h2: "Your Hen Weekend, Your Way",
        paras: [
          "Arrive by high-speed boat, settle into your exclusive lodge and start the celebrations.",
          "Enjoy drinks in your own traditional Irish bar, relax in the beach sauna, take to the water for kayaking or paddleboarding, and arrange evening entertainment for your group.",
        ],
        image: R("hen-party.jpg"),
        imageAlt: "A hen party waving from the boat as they arrive at the island",
      },
      {
        h2: "Popular Hen Party Extras",
        list: [
          "Guided sea kayaking.",
          "Stand-up paddleboarding.",
          "Beach sauna experiences.",
          "Hot tub access at scheduled times.",
          "Chinese banquet catering.",
          "Two-piece live band.",
          "Private bar and cocktail-style bar.",
          "Optional tailored group activities.",
        ],
      },
      {
        h2: "Why Choose Collanmore for Your Hen Party?",
        paras: [
          "Your group enjoys privacy, flexibility, spectacular scenery and the convenience of having accommodation and optional activities organised through one experienced team.",
        ],
        image: R("lodge-deck-night.jpg"),
        imageAlt: "The lodge deck lit by lanterns after dark",
      },
    ],
    finalHeading: "Plan Your Hen Party",
  },

  stag: {
    path: "/stag-parties-westport/",
    eyebrow: "Stag Parties",
    seo: {
      title: "Stag Parties Westport | Private Island Stag Weekends Mayo",
      description:
        "Book a private island stag weekend near Westport. Exclusive accommodation for 22 with private bar, kayaking, sea safari, sauna and group entertainment.",
      path: "/stag-parties-westport/",
    },
    h1: "The Ultimate Private Island Stag Weekend Near Westport",
    heroImage: R("rib-wake.jpg"),
    heroImageAlt: "Aerial view of our RIB carving through the islands of Clew Bay",
    heroParas: [
      "Forget the ordinary stag weekend.",
      "Gather your friends, board the boat at Rosmoney Pier and head for a private island adventure in Clew Bay.",
      "Collanmore Island Lodge provides exclusive accommodation for up to 22 guests, complete with private bars, spacious social areas and optional adventure activities.",
      "Spend your day kayaking, paddleboarding or exploring the coastline before enjoying an evening with your group at the lodge.",
    ],
    cta: { label: "Get a Stag Weekend Quote", to: "/contact/" },
    sections: [
      {
        h2: "Adventure by Day, Private Bar by Night",
        paras: [
          "Combine outdoor adventure with the freedom of your own exclusive group accommodation.",
          "Choose from guided water activities, sea safari experiences, sauna sessions and optional evening entertainment.",
        ],
        image: R("rib-sunset-guests.jpg"),
        imageAlt: "Guests laughing aboard the RIB at sunset",
      },
      {
        h2: "A Stag Weekend Built Around Your Group",
        paras: ["Our team can help coordinate your accommodation, boat transfers, activities, catering and entertainment."],
        image: R("lodge-bar.jpg"),
        imageAlt: "Tom's Bar — your private Irish bar for the weekend",
      },
    ],
    finalHeading: "Get a Stag Weekend Quote",
  },

  birthdays: {
    path: "/private-island-celebrations/",
    eyebrow: "Birthdays & Celebrations",
    seo: {
      title: "Birthday Party Venues Mayo | Private Island Celebrations",
      description:
        "Celebrate a milestone birthday, reunion or special occasion at an exclusive island lodge near Westport, sleeping up to 22 with private bars and activities.",
      path: "/private-island-celebrations/",
    },
    h1: "Celebrate Life's Special Moments on a Private Island",
    heroImage: R("sumo-lawn.jpg"),
    heroImageAlt: "Sumo suit wrestling on the lodge lawn during a birthday weekend",
    heroParas: [
      "Some occasions deserve somewhere extraordinary.",
      "Whether you're celebrating a 30th, 40th, 50th or 60th birthday, a family reunion, anniversary or weekend with lifelong friends, Collanmore Island Lodge provides an unforgettable setting.",
      "Gather up to 22 overnight guests for a private island celebration with stunning scenery, comfortable accommodation, your own bar facilities and optional entertainment.",
    ],
    cta: { label: "Plan Your Celebration", to: "/contact/" },
    sections: [
      {
        h2: "A Celebration That's Entirely Yours",
        paras: [
          "Plan a weekend of relaxed dining, drinks, music, adventure and time together.",
          "Choose from catering, live music, sauna sessions and water activities, or simply enjoy the freedom of your island surroundings.",
        ],
        image: R("lodge-living.jpg"),
        imageAlt: "The communal living and dining space inside the lodge",
      },
    ],
    finalHeading: "Plan Your Celebration",
  },

  corporate: {
    path: "/corporate-retreats-mayo/",
    eyebrow: "Corporate Retreats",
    seo: {
      title: "Corporate Retreats Mayo | Island Team Building Westport",
      description:
        "Host a corporate retreat or team-building event on Collanmore Island near Westport, with exclusive accommodation, kayaking, sea safari and tailored activities.",
      path: "/corporate-retreats-mayo/",
    },
    h1: "Corporate Retreats and Team Building with a Difference",
    heroImage: R("lodge-deck-day.jpg"),
    heroImageAlt: "The lodge sun deck overlooking Clew Bay — a fresh setting for team thinking",
    heroParas: [
      "Take your team away from the distractions of the everyday workplace and discover a fresh perspective on Collanmore Island.",
      "Our exclusive-use island lodge provides a distinctive setting for team building, leadership retreats, company celebrations and small corporate gatherings.",
      "Just minutes from Westport, the island offers a combination of private accommodation, communal meeting and social spaces, outdoor activities and spectacular natural surroundings.",
    ],
    cta: { label: "Enquire About a Corporate Retreat", to: "/contact/" },
    sections: [
      {
        h2: "Connect, Collaborate and Recharge",
        paras: ["Create a programme that balances productive group time with adventure, relaxation and social experiences."],
        image: R("lodge-living.jpg"),
        imageAlt: "Informal group meeting and social space inside the lodge",
      },
      {
        h2: "Corporate Experiences",
        list: [
          "Exclusive lodge accommodation.",
          "Guided kayaking and paddleboarding.",
          "Sea safari experiences.",
          "Team challenges.",
          "Beach sauna and wellness activities.",
          "Informal group meeting spaces.",
          "Catering and entertainment by arrangement.",
        ],
      },
      {
        h2: "Bespoke Corporate Packages",
        paras: ["We can help coordinate a programme suited to your group's objectives, available time and budget."],
        image: R("lodge-bar.jpg"),
        imageAlt: "Evening drinks at Tom's Bar during a corporate retreat",
      },
    ],
    finalHeading: "Enquire About a Corporate Retreat",
  },


  adventure: {
    path: "/adventure-activities-westport/",
    partner: true,
    eyebrow: "Adventure Activities",
    seo: {
      title: "Kayaking & Adventure Activities Westport | Clew Bay Mayo",
      description:
        "Discover kayaking, stand-up paddleboarding, sea safaris and private island adventures in Clew Bay near Westport with The Adventure Islands.",
      path: "/adventure-activities-westport/",
    },
    h1: "Adventure Activities in Clew Bay, Westport",
    heroImage: R("kayak-aerial.jpg"),
    heroImageAlt: "Kayakers paddling the clear sheltered waters of Clew Bay beside the island shore",
    heroParas: [
      "Discover the spectacular coastline of Clew Bay with The Adventure Islands, the adventure activity partner of Collanmore Island Lodge.",
      "Our location offers an exciting range of guided water and island experiences for visitors, private groups and lodge guests.",
    ],
    cta: { label: "Enquire About Adventure Activities", to: "/contact/" },
    sections: [
      {
        h2: "Sea Kayaking",
        paras: ["Explore the sheltered coastal waters and beautiful island surroundings with a guided sea kayaking experience."],
        image: R("kayak-pair.jpg"),
        imageAlt: "Guests cheering on a kayak during a Clew Bay paddling adventure",
      },
      {
        h2: "Stand-Up Paddleboarding",
        paras: ["Experience Clew Bay from the water with stand-up paddleboarding, a fun and memorable activity for groups."],
      },
      {
        h2: "Sea Safari and Wildlife Tours",
        paras: [
          "Discover the scenery, coastline and wildlife of Clew Bay on an exhilarating guided boat adventure.",
          "Wildlife sightings depend on natural conditions and cannot be guaranteed.",
        ],
        image: R("rib-wake.jpg"),
        imageAlt: "Aerial view of our RIB carving through the islands of Clew Bay",
      },
      {
        h2: "Island Team Activities",
        paras: ["Bring your group together with tailored island challenges and outdoor experiences."],
        image: R("sumo-lawn.jpg"),
        imageAlt: "Tailored island team challenges on the lodge lawn",
      },
      {
        h2: "Kayaking, Paddleboarding and Sauna Packages",
        paras: [
          "Combine guided water activities with a relaxing beach sauna session.",
          "Experiences are subject to weather, availability, group requirements and safety conditions.",
        ],
        image: R("hottub-bubbles.webp"),
        imageAlt: "The outdoor hot tub bubbling away beside the deck, with Clew Bay beyond",
      },
    ],
    finalHeading: "Enquire About Adventure Activities",
  },

  sauna: {
    path: "/beach-sauna-westport/",
    eyebrow: "Beach Sauna & Wellness",
    seo: {
      title: "Beach Sauna Westport | Island Wellness Experience Mayo",
      description:
        "Enjoy a beach sauna overlooking Clew Bay near Westport, with optional kayaking, paddleboarding and group wellness experiences on Collanmore Island.",
      path: "/beach-sauna-westport/",
    },
    h1: "Beach Sauna and Island Wellness in Clew Bay",
    heroImage: R("sauna-group.webp"),
    heroImageAlt: "A group enjoying the wood-burning beach sauna on Collanmore Island, with the sea view through the window",
    heroParas: [
      "Escape the everyday and experience the restorative atmosphere of the Atlantic coast.",
      "Our beach sauna offers a distinctive wellness experience in a spectacular island setting overlooking Clew Bay.",
      "Relax in the warmth of the sauna, take in the sea air and enjoy the natural beauty of the Mayo coastline.",
    ],
    cta: { label: "Book or Enquire About the Beach Sauna", to: "/contact/" },
    sections: [
      {
        h2: "Sauna Insula — Our Beach Sauna Experience",
        paras: [
          "Whether you're planning a relaxing group outing or adding a wellness session to an adventurous weekend, the beach sauna is a memorable part of the Collanmore experience.",
        ],
        image: R("island-sunset.jpg"),
        imageAlt: "Guests watching the sun set over Clew Bay from the island",
      },
      {
        h2: "Combine Adventure and Relaxation",
        paras: ["Pair a guided kayaking or paddleboarding experience with time in the beach sauna."],
        image: R("kayak-aerial.jpg"),
        imageAlt: "Kayakers on the sheltered waters of Clew Bay",
      },
      {
        h2: "Island Wellness for Groups",
        paras: ["Ideal for friends, corporate teams, wellness groups and guests staying at the lodge."],
        image: R("hottub-bubbles.webp"),
        imageAlt: "The outdoor hot tub bubbling away beside the deck with Clew Bay beyond",
      },
    ],
    finalHeading: "Book or Enquire About the Beach Sauna",
  },

  weddings: {
    path: "/private-island-weddings-mayo/",
    eyebrow: "Weddings & Private Events",
    seo: {
      title: "Private Island Wedding Venue Mayo | Collanmore Island Lodge",
      description:
        "Explore a distinctive private island wedding and celebration venue in Clew Bay near Westport, with exclusive lodge accommodation and tailored event options.",
      path: "/private-island-weddings-mayo/",
    },
    h1: "A Private Island Setting for Your Special Day",
    heroImage: R("island-wedding-aerial.jpg"),
    heroImageAlt: "Aerial view of a wedding gathering on Collanmore Island with Clew Bay beyond",
    heroParas: [
      "Celebrate your wedding or special event surrounded by the natural beauty of Clew Bay.",
      "Collanmore Island Lodge offers a distinctive and intimate setting for couples and groups seeking a celebration away from traditional venues.",
      "From island ceremonies and relaxed gatherings to bespoke private events, our team can discuss the possibilities for your occasion.",
    ],
    cta: { label: "Enquire About Your Island Wedding", to: "/contact/" },
    sections: [
      {
        h2: "A Wedding Venue Unlike the Ordinary",
        paras: ["Arrive by boat, enjoy stunning Atlantic views and celebrate with your guests in an unforgettable island setting."],
        image: R("wedding-couple.jpg"),
        imageAlt: "A newlywed couple kissing on the pontoon beside the Collanmore Island Lodge RIB",
      },
      {
        h2: "Tailored Island Celebrations",
        paras: [
          "We can discuss exclusive lodge accommodation, event spaces, boat transfers, catering coordination and entertainment options.",
          "Wedding event capacity, ceremony arrangements, pricing and any required permissions must be confirmed directly before booking.",
        ],
        image: R("rib-jetty.jpg"),
        imageAlt: "The boat that carries wedding guests to the island, moored at the jetty",
      },
    ],
    finalHeading: "Enquire About Your Island Wedding",
  },
};

// Breadcrumb structured data for every landing page (Home › Page).
import { breadcrumb } from "../../components/Seo";

const pageCrumbs = {
  lodge: "The Island Lodge",
  hen: "Hen Parties",
  stag: "Stag Parties",
  birthdays: "Celebrations",
  corporate: "Corporate Retreats",
  traitors: "Traitors Island",
  adventure: "Adventure Activities",
  sauna: "Beach Sauna & Wellness",
  weddings: "Weddings & Private Events",
};

Object.entries(pageCrumbs).forEach(([key, label]) => {
  if (pages[key] && pages[key].seo) {
    pages[key].seo.jsonLd = [breadcrumb(pages[key].seo.path, label)];
  }
});

function U_S(id) {
  return `https://images.unsplash.com/${id}?q=80&w=1800&auto=format&fit=crop`;
}
